import React, { useCallback, useEffect, useRef, useState } from 'react'
import Layout from '../../layout'
import Loader from '../../components/common/loader'
import Breadcrums from '../../components/common/breadcrumbs'
import notFound from '../../assets/company/img/no-record-found.png'
import { GetLogDates, GetLogs } from '../../services/logsService'

const LEVELS = [
    { value: '', label: 'All levels' },
    { value: 'error', label: 'Error' },
    { value: 'warn', label: 'Warn' },
    { value: 'info', label: 'Info' },
    { value: 'debug', label: 'Debug' },
]

const PER_PAGE_OPTIONS = [50, 100, 200]

// A search can scan 8 MB without a hit, so an empty page is not the end of the
// day. Chain a few pages before handing control back to the scroll listener.
const EMPTY_PAGE_CHAIN_LIMIT = 8

const formatBytes = (bytes) => {
    const size = Number(bytes)
    if (!size || isNaN(size)) return '0 B'
    if (size < 1024) return `${size} B`
    if (size < 1024 * 1024) return `${(size / 1024).toFixed(1)} KB`
    return `${(size / (1024 * 1024)).toFixed(1)} MB`
}

// "2026-10-01" is a plain calendar day, so it is split rather than passed to
// Date(), which would shift it by the browser's timezone.
const parseDay = (value) => {
    const [year, month, day] = String(value || '').split('-').map(Number)
    if (!year || !month || !day) return null
    return new Date(year, month - 1, day)
}

const MONTHS = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec']

const formatDay = (value) => {
    const date = parseDay(value)
    if (!date) return value
    return `${MONTHS[date.getMonth()]} ${date.getDate()}, ${date.getFullYear()}`
}

const relativeDay = (value) => {
    const date = parseDay(value)
    if (!date) return ''
    const today = new Date()
    today.setHours(0, 0, 0, 0)
    const diff = Math.round((today - date) / 86400000)
    if (diff === 0) return 'Today'
    if (diff === 1) return 'Yesterday'
    return `${diff} days ago`
}

// Time comes back as "2026-10-01 00:20:49"; only the clock part is useful once
// the day is already picked above.
const timeOf = (value) => {
    const parts = String(value || '').split(' ')
    return parts.length > 1 ? parts[1] : value
}

const statusClass = (status) => {
    const code = Number(status)
    if (code >= 500) return 'danger'
    if (code >= 400) return 'warning'
    if (code >= 200 && code < 300) return 'success'
    return 'muted'
}

const LogsPage = () => {

    const breadcrumbs = [{ title: "Dashboard", url: "/admin/dashboard" }, { title: "Logs", url: "" }]

    const [dates, setDates] = useState([])
    const [datesLoader, setDatesLoader] = useState(true)
    const [selectedDate, setSelectedDate] = useState('')

    const [level, setLevel] = useState('')
    const [searchInput, setSearchInput] = useState('')
    const [search, setSearch] = useState('')
    const [perPage, setPerPage] = useState(50)

    const [rows, setRows] = useState([])
    const [loader, setLoader] = useState(false)
    const [loadingMore, setLoadingMore] = useState(false)
    const [hasMore, setHasMore] = useState(false)
    const [scannedBytes, setScannedBytes] = useState(0)
    const [fileSize, setFileSize] = useState(0)
    const [expanded, setExpanded] = useState({})
    const [error, setError] = useState('')

    // The scroll handler fires far more often than React re-renders, so the
    // paging state it reads lives in a ref as well.
    const cursorRef = useRef(null)
    const hasMoreRef = useRef(false)
    const fetchingRef = useRef(false)
    const scrollRef = useRef(null)

    useEffect(() => {
        GetLogDates().then(data => {
            // the admin APIs wrap their payload in `response`
            const list = data?.data?.response?.dates || data?.data?.data?.dates || []
            setDates(list)
            setSelectedDate(prev => prev || (list.length ? list[0].date : ''))
            setDatesLoader(false)
        }).catch(err => {
            console.log("error ====> ", err)
            setError(err?.response?.data?.message || 'Could not load log dates')
            setDatesLoader(false)
        })
    }, [])

    const fetchLogs = useCallback(async ({ reset }) => {
        if (!selectedDate || fetchingRef.current) return
        fetchingRef.current = true
        if (reset) {
            setLoader(true)
            cursorRef.current = null
            hasMoreRef.current = false
        } else {
            setLoadingMore(true)
        }
        setError('')

        try {
            let collected = []
            let attempts = 0
            let more = true
            let scanned = 0

            // Keep pulling while the server hands back an empty page it has not
            // finished scanning, otherwise a search looks like "no results".
            do {
                const payload = {
                    date: selectedDate,
                    per_page: perPage,
                    cursor: cursorRef.current,
                }
                if (level) payload.level = level
                if (search) payload.search = search

                const data = await GetLogs(payload)
                const body = data?.data?.response || data?.data?.data || {}
                collected = collected.concat(body.rows || [])
                cursorRef.current = body.next_cursor != null ? body.next_cursor : null
                more = Boolean(body.has_more)
                scanned += Number(body.scanned_bytes) || 0
                if (reset) setFileSize(Number(body.file_size) || 0)
                attempts += 1
            } while (collected.length === 0 && more && attempts < EMPTY_PAGE_CHAIN_LIMIT)

            hasMoreRef.current = more
            setHasMore(more)
            setScannedBytes(prev => (reset ? scanned : prev + scanned))
            setRows(prev => (reset ? collected : prev.concat(collected)))
        } catch (err) {
            console.log("error ====> ", err)
            setError(err?.response?.data?.message || 'Could not load logs')
            hasMoreRef.current = false
            setHasMore(false)
        } finally {
            fetchingRef.current = false
            setLoader(false)
            setLoadingMore(false)
        }
    }, [selectedDate, level, search, perPage])

    // Date, level, search and page size each start a fresh read of the file.
    useEffect(() => {
        if (!selectedDate) return
        setRows([])
        setExpanded({})
        fetchLogs({ reset: true })
        if (scrollRef.current) scrollRef.current.scrollTop = 0
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [selectedDate, level, search, perPage])

    const handleScroll = (event) => {
        const el = event.target
        if (el.scrollHeight - el.scrollTop - el.clientHeight > 200) return
        if (!hasMoreRef.current || fetchingRef.current) return
        fetchLogs({ reset: false })
    }

    const submitSearch = (event) => {
        event.preventDefault()
        setSearch(searchInput.trim())
    }

    const resetFilters = () => {
        setSearchInput('')
        setSearch('')
        setLevel('')
        setPerPage(50)
    }

    const toggleRow = (key) => {
        setExpanded(prev => ({ ...prev, [key]: !prev[key] }))
    }

    const selectedDay = dates.find(d => d.date === selectedDate)

    return (
        <Layout>
            <Breadcrums data={breadcrumbs} />
            <div className="animation_fade">

                <div className="card custom-card overflow-hidden mb-3">
                    <div className="card-body">
                        <div className="d-flex justify-content-between align-items-center mb-3">
                            <h6 className="main-content-label mb-0">Server Logs</h6>
                            <span className="logs-meta">Last 7 days</span>
                        </div>

                        {datesLoader ? <Loader /> : (
                            dates.length ? (
                                <div className="logs-days">
                                    {dates.map((day) => (
                                        <button
                                            type="button"
                                            key={day.date}
                                            className={`logs-day ${day.date === selectedDate ? 'active' : ''}`}
                                            onClick={() => setSelectedDate(day.date)}
                                        >
                                            <span className="logs-day-date">{formatDay(day.date)}</span>
                                            <span className="logs-day-rel">{relativeDay(day.date)}</span>
                                            <span className="logs-day-stats">
                                                <span title="Combined log size">{formatBytes(day.combined_size)}</span>
                                                {Number(day.error_size) > 0 ?
                                                    <span className="logs-day-errors" title="Error log size">{formatBytes(day.error_size)} errors</span>
                                                    : null}
                                                {day.compressed ? <span className="logs-day-gz" title="Rolled over and gzipped">gz</span> : null}
                                            </span>
                                        </button>
                                    ))}
                                </div>
                            ) : <p className="logs-meta mb-0">No log files found.</p>
                        )}
                    </div>
                </div>

                <div className="card custom-card overflow-hidden">
                    <div className="card-body">
                        <form className="logs-filters" onSubmit={submitSearch}>
                            <div className="logs-search">
                                <i className="fa fa-search"></i>
                                <input
                                    type="text"
                                    className="form-control"
                                    placeholder="Search message, stack, URL, IP, order id..."
                                    value={searchInput}
                                    onChange={(e) => setSearchInput(e.target.value)}
                                />
                                {searchInput ?
                                    <button type="button" className="logs-search-clear" onClick={() => { setSearchInput(''); setSearch('') }} title="Clear">
                                        <i className="fa fa-times"></i>
                                    </button>
                                    : null}
                            </div>
                            <button type="submit" className="btn btn-main-primary" disabled={!selectedDate}>Search</button>
                            <select className="form-control logs-select" value={level} onChange={(e) => setLevel(e.target.value)}>
                                {LEVELS.map(option => (
                                    <option key={option.value} value={option.value}>{option.label}</option>
                                ))}
                            </select>
                            <select className="form-control logs-select" value={perPage} onChange={(e) => setPerPage(Number(e.target.value))}>
                                {PER_PAGE_OPTIONS.map(size => (
                                    <option key={size} value={size}>{size} / page</option>
                                ))}
                            </select>
                            <button type="button" className="btn btn-outline-primary" onClick={resetFilters}>Reset</button>
                            <button type="button" className="btn btn-outline-primary" onClick={() => fetchLogs({ reset: true })} disabled={!selectedDate || loader}>
                                <i className="fa fa-refresh me-1"></i>Refresh
                            </button>
                        </form>

                        {error ? <div className="alert alert-danger py-2 mb-3">{error}</div> : null}

                        <div className="logs-summary">
                            <span><b>{rows.length}</b> {rows.length === 1 ? 'entry' : 'entries'} loaded</span>
                            {selectedDay ? <span>{formatDay(selectedDay.date)}</span> : null}
                            {fileSize ? <span>File {formatBytes(fileSize)}</span> : null}
                            {scannedBytes ? <span>Scanned {formatBytes(scannedBytes)}</span> : null}
                            {search ? <span className="logs-summary-chip">search: {search}</span> : null}
                            {level ? <span className="logs-summary-chip">level: {level}</span> : null}
                        </div>

                        <div className="logs-stream" ref={scrollRef} onScroll={handleScroll}>
                            {loader ? <div className="py-4"><Loader /></div> : (
                                rows.length ? (
                                    <>
                                        {rows.map((row, index) => {
                                            const key = `${row.time}-${index}`
                                            const isOpen = Boolean(expanded[key])
                                            return (
                                                <div className={`logs-row ${row.level || 'info'}`} key={key}>
                                                    <div
                                                        className={`logs-row-main ${row.stack ? 'expandable' : ''}`}
                                                        onClick={() => row.stack && toggleRow(key)}
                                                    >
                                                        <span className="logs-time">{timeOf(row.time)}</span>
                                                        <span className={`logs-level ${row.level || 'info'}`}>{row.level || 'info'}</span>
                                                        <div className="logs-body">
                                                            <span className="logs-message">{row.message}</span>
                                                            {row.request ? (
                                                                <span className="logs-request">
                                                                    <span className="logs-chip method">{row.request.method}</span>
                                                                    <span className="logs-chip url" title={row.request.url}>{row.request.url}</span>
                                                                    <span className={`logs-chip status ${statusClass(row.request.status)}`}>{row.request.status}</span>
                                                                    {row.request.duration_ms != null ? <span className="logs-chip">{row.request.duration_ms} ms</span> : null}
                                                                    {row.request.ip ? <span className="logs-chip">{row.request.ip}</span> : null}
                                                                    {row.request.tag ? <span className="logs-chip tag">{row.request.tag}</span> : null}
                                                                </span>
                                                            ) : null}
                                                        </div>
                                                        {row.stack ?
                                                            <span className="logs-expand" title={isOpen ? 'Hide stack trace' : 'Show stack trace'}>
                                                                <i className={`fa fa-chevron-${isOpen ? 'up' : 'down'}`}></i>
                                                            </span>
                                                            : null}
                                                    </div>
                                                    {row.stack && isOpen ? <pre className="logs-stack">{row.stack}</pre> : null}
                                                </div>
                                            )
                                        })}

                                        {loadingMore ? <div className="py-3"><Loader /></div> : null}
                                        {!hasMore && !loadingMore ?
                                            <p className="logs-end">Start of {selectedDay ? formatDay(selectedDay.date) : 'the day'} reached</p>
                                            : null}
                                    </>
                                ) : (
                                    <div className="text-center py-5">
                                        <img src={notFound} style={{ width: "90px" }} alt="Not Found" />
                                        <p className="mt-3 mb-0">{search || level ? 'No entries match these filters' : 'No log entries for this day'}</p>
                                    </div>
                                )
                            )}
                        </div>
                    </div>
                </div>
            </div>
        </Layout>
    )
}

export default LogsPage
